'use client';

import { useEffect, useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Tour, tourFromFirestore } from '@/types';
import { translateTours } from '@/lib/tourTranslation';
import TourCard from '@/components/tours/TourCard';
import { Search, MapPin, Filter, X, Map } from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageProvider';

const CITIES = ['Barcelona', 'Madrid', 'Sevilla', 'Valencia', 'Málaga', 'Bilbao', 'Granada'];

function ToursContent() {
  const { t, locale } = useTranslation();
  const searchParams = useSearchParams();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 25]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchTours = async () => {
      setLoading(true);
      try {
        let q = query(collection(db, 'tours'), where('status', '==', 'approved'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        let fetchedTours = snapshot.docs.map(tourFromFirestore);

        // Apply translations for current locale
        await translateTours(fetchedTours, locale);

        if (selectedCity) fetchedTours = fetchedTours.filter(t => t.city.toLowerCase() === selectedCity.toLowerCase());
        if (selectedLanguage) fetchedTours = fetchedTours.filter(t => t.languages.some(l => l.toLowerCase().includes(selectedLanguage.toLowerCase())));
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          fetchedTours = fetchedTours.filter(t => t.title.toLowerCase().includes(term) || t.description.toLowerCase().includes(term) || t.city.toLowerCase().includes(term) || t.country.toLowerCase().includes(term) || t.province.toLowerCase().includes(term));
        }
        fetchedTours = fetchedTours.filter(t => t.price >= priceRange[0] && t.price <= priceRange[1]);

        setTours(fetchedTours);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [selectedCity, selectedLanguage, searchTerm, priceRange, locale]);

  const clearFilters = () => { setSearchTerm(''); setSelectedCity(''); setSelectedLanguage(''); setPriceRange([0, 25]); };
  const hasActiveFilters = selectedCity || selectedLanguage || searchTerm || priceRange[0] > 0 || priceRange[1] < 50;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-900 border-b dark:border-gray-700 pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('tours.title')}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">{t('tours.subtitle')}</p>
          
          <div className="mt-6 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder={t('tours.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="input pl-10" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-primary text-white border-primary' : 'border-gray-300 dark:border-gray-700 hover:border-primary dark:text-gray-300'}`}>
              <Filter className="w-5 h-5" /><span className="hidden sm:inline">{t('tours.filters')}</span>
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tours.priceMax') || `Max: ${priceRange[1]}€`}</label>
                  <input type="range" min="0" max="25" value={priceRange[1]} onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])} className="w-full" />
                </div>
                <div className="flex items-end">
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700"><X className="w-4 h-4" />{t('tours.clearFilters') || 'Clear'}</button>}
                </div>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedCity && <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"><MapPin className="w-3 h-3" />{selectedCity}<button onClick={() => setSelectedCity('')}><X className="w-3 h-3" /></button></span>}
              {selectedLanguage && <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm">{selectedLanguage}<button onClick={() => setSelectedLanguage('')}><X className="w-3 h-3" /></button></span>}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 text-gray-600 dark:text-gray-300">{loading ? t('common.loading') : `${tours.length} ${tours.length === 1 ? t('home.tourFound') : t('home.toursFound')}`}</div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="card animate-pulse"><div className="h-48 bg-gray-200 dark:bg-gray-700" /><div className="p-4 space-y-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" /><div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4" /></div></div>)}
          </div>
        ) : tours.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.map((tour) => <TourCard key={tour.id} tour={tour} />)}
          </div>
        ) : (
          <div className="text-center py-16">
            <Map className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('tours.noResults')}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{t('tours.noResultsDesc')}</p>
            {hasActiveFilters && <button onClick={clearFilters} className="btn-outline">{t('tours.clearFilters') || 'Clear'}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ToursPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>}>
      <ToursContent />
    </Suspense>
  );
}
